import { secp256k1 } from '@noble/curves/secp256k1'
import { keccak_256 } from '@noble/hashes/sha3'
import { bytesToHex, concatBytes, hexToBytes, utf8ToBytes } from '@noble/hashes/utils'

export function toHexMessage(message) {
  return '0x' + Array.from(new TextEncoder().encode(message))
    .map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function normalizeSignatureV(signature) {
  const hex = String(signature || '').trim().toLowerCase()
  const prefixed = hex.startsWith('0x') ? hex : `0x${hex}`
  if (!/^0x[0-9a-f]{130}$/.test(prefixed)) return prefixed
  const v = Number.parseInt(prefixed.slice(-2), 16)
  let next = v
  if (v === 0 || v === 1) next = v + 27
  else if (v >= 35) next = 27 + ((v - 35) % 2)
  if ((next !== 27 && next !== 28) || next === v) return prefixed
  return `${prefixed.slice(0, -2)}${next.toString(16).padStart(2, '0')}`
}

function recoverAddress(message, signature) {
  const normalized = normalizeSignatureV(signature)
  const bytes = hexToBytes(normalized.slice(2))
  const v = bytes[64] >= 27 ? bytes[64] - 27 : bytes[64]
  const digest = keccak_256(concatBytes(
    utf8ToBytes(`\u0019Ethereum Signed Message:\n${utf8ToBytes(message).length}`),
    utf8ToBytes(message)
  ))
  const pub = secp256k1.Signature
    .fromCompact(bytes.slice(0, 64))
    .addRecoveryBit(v)
    .recoverPublicKey(digest)
    .toRawBytes(false)
  return `0x${bytesToHex(keccak_256(pub.slice(1)).slice(-20))}`
}

export function signatureMatches(message, signature, address) {
  try {
    const recovered = recoverAddress(message, signature)
    if (recovered === String(address || '').toLowerCase()) return normalizeSignatureV(signature)
  } catch {
    return ''
  }
  return ''
}

export function isDefiWallet(provider) {
  return Boolean(
    provider?.isDeficonnectProvider
    || provider?.isCryptoCom
    || provider?.isOnchain
    || /DeFiWallet/i.test(navigator.userAgent || '')
  )
}

export async function signWalletLoginMessage(provider, address, message) {
  const hexMessage = toHexMessage(message)
  const attempts = isDefiWallet(provider)
    ? [[hexMessage, address], [address, hexMessage], [message, address], [address, message]]
    : [[hexMessage, address], [message, address], [address, hexMessage], [address, message]]
  let lastError = null
  for (const params of attempts) {
    try {
      const signature = await provider.request({ method: 'personal_sign', params })
      const matched = signatureMatches(message, signature, address)
      if (matched) return matched
    } catch (error) {
      if (error?.code === 4001) throw error
      lastError = error
    }
  }
  const { translateApiMessage } = await import('@/utils/apiMessage')
  throw lastError || new Error(translateApiMessage('签名校验失败'))
}
